import { Pencil } from 'lucide-react'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui'
import {
  BulkEditTransactionsDialog,
  type Transaction,
} from '@/modules/projects'
import { useDialogActions } from '@/stores'
import { cn } from '@/utils'
import type { TransactionsTableVariant } from './columns'

interface BulkEditDropdownProps {
  getTransactions: () => Transaction[]
  variant?: TransactionsTableVariant
}

// Editable bulk fields per variant. The checks list drops deterministic /
// N/A fields (account_type, merchant, category, payment_channel,
// share_number) and adds check_number.
const TRANSACTION_FIELDS: [string, string][] = [
  ['accountNumber', 'Account Number'],
  ['accountType', 'Account Type'],
  ['category', 'Category'],
  ['merchant', 'Merchant'],
  ['paymentChannel', 'Payment Channel'],
  ['shareNumber', 'Share Number'],
  ['year', 'Year'],
  ['institution', 'Institution'],
  ['accountOwner', 'Account Owner'],
]

const CHECK_FIELDS: [string, string][] = [
  ['accountNumber', 'Account Number'],
  ['accountOwner', 'Account Owner'],
  ['checkNumber', 'Check Number'],
  ['institution', 'Institution'],
  ['year', 'Year'],
]

const sortByLabel = (fields: [string, string][]) =>
  [...fields].sort(([, a], [, b]) => a.localeCompare(b))

export const BulkEditDropdown = ({
  getTransactions,
  variant = 'transactions',
}: BulkEditDropdownProps) => {
  const { openDialog } = useDialogActions()

  const fields = sortByLabel(
    variant === 'checks' ? CHECK_FIELDS : TRANSACTION_FIELDS,
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          theme="outline"
          size="sm"
          className={cn('h-8 gap-1 font-normal')}
        >
          <Pencil className="size-3.5" />
          Edit
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {fields.map(([field, label]) => (
          <DropdownMenuItem
            key={field}
            onClick={() =>
              openDialog(BulkEditTransactionsDialog.id, {
                field,
                transactions: getTransactions(),
                variant,
              })
            }
          >
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
